// src/components/auth/MicrosoftSignInButton.tsx
// Microsoft Sign-In button using Microsoft Identity Platform

import React, { useCallback } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { Button, CircularProgress } from '@mui/material';
import { OAuthButtonProps, OAuthError } from '../../types/oauth';
import { OAUTH_CONFIG, MICROSOFT_OAUTH_CONFIG } from '../../config/oauth';
import oauthService from '../../services/oauthService';
import clarityService from '../../services/clarityService';

// Microsoft icon SVG
const MicrosoftIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: MICROSOFT_OAUTH_CONFIG.clientId,
    authority: MICROSOFT_OAUTH_CONFIG.options?.authority as string || 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
    storeAuthStateInCookie: false,
  },
};

// MSAL instance (singleton)
let msalInstance: PublicClientApplication | null = null;

const getMsalInstance = async (): Promise<PublicClientApplication> => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  return msalInstance;
};

/**
 * Microsoft Sign-In Button Component
 * Custom styled button that matches your UI
 */
const MicrosoftSignInButton: React.FC<OAuthButtonProps> = ({
  onSuccess,
  onError,
  disabled = false,
  variant = 'outlined',
  fullWidth = true,
  text,
  context = 'signin',
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  // Default button text based on context
  const buttonText = text || (context === 'signup' ? 'Sign up with Microsoft' : 'Sign in with Microsoft');

  /**
   * Handle Microsoft Sign-In
   */
  const handleMicrosoftLogin = useCallback(async () => {
    setIsLoading(true);
    clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_STARTED);

    try {
      const msalInstance = await getMsalInstance();

      // Login with popup
      const loginResponse = await msalInstance.loginPopup({
        scopes: MICROSOFT_OAUTH_CONFIG.scopes,
        prompt: 'select_account',
      });

      if (!loginResponse.accessToken) {
        throw new Error('No access token received from Microsoft');
      }

      // Send token to backend for verification
      const { user, isNewUser } = await oauthService.authenticateWithProvider({
        provider: 'microsoft',
        credential: loginResponse.accessToken,
      });

      if (isNewUser) {
        clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.SIGNUP_SUCCESS);
        clarityService.upgrade('New User OAuth Registration');
      } else {
        clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_SUCCESS);
      }

      onSuccess(user, isNewUser);
    } catch (error: unknown) {
      clarityService.event(OAUTH_CONFIG.ANALYTICS_EVENTS.LOGIN_FAILED);

      console.error('Microsoft login error:', error);

      const oauthError: OAuthError =
        error && typeof error === 'object' && 'code' in error
          ? (error as OAuthError)
          : {
              code: 'AUTH_FAILED',
              message: error instanceof Error ? error.message : OAUTH_CONFIG.ERRORS.AUTHENTICATION_FAILED,
              originalError: error,
            };

      onError(oauthError);
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess, onError]);

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled || isLoading}
      onClick={handleMicrosoftLogin}
      startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <MicrosoftIcon />}
      sx={{
        py: 1.5,
        textTransform: 'none',
        fontSize: '0.9375rem',
        fontWeight: 500,
        borderColor: 'divider',
        color: 'text.primary',
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'text.secondary',
        },
      }}
    >
      {isLoading ? 'Signing in...' : buttonText}
    </Button>
  );
};

export default MicrosoftSignInButton;
